/**
 * Deleting a whole service (vfs-service src/projects/serviceDelete.js, routes in src/routes/projectServices.routes.js):
 *
 * - `getDeletePlan(projectId, service)` → `{ service, path, readOnly, cleanup: { pins, defaultApp, deploySwitch, repoFlags,
 *   vars, secrets, jobs }, references: [{ service, path, line?, kind, text }], dataStores: { deleted, note } }`
 * - `deleteService(projectId, service, { confirm })` → `{ service, id, commit, stopped, cleaned, references, dataStores }`
 *   (editors; 400 `confirm_required`, 403 `read_only`, 409 `read_only_repo`)
 * - `listDeletedServices(projectId)` → `[{ id, service, deletedAt, by, commit }]`
 * - `restoreDeletedService(projectId, id)` → `{ service, id, commit, restored }` (409 `exists`, 404 `not_found`)
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const url = (projectId, path) => `${base()}/api/v1/projects/${encodeURIComponent(projectId)}/services${path}`;
const short = (service) => String(service || '').replace(/^services\//, '');

export class ServiceDeleteError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ServiceDeleteError';
    this.status = status;
    this.code = body?.code || null;
  }
}

async function call(target, { method = 'GET', body } = {}) {
  if (!base()) { throw new ServiceDeleteError('vfs-service is not configured', 0, null); }
  const json = body !== undefined;
  const response = await getAuthorizedFetch()(target, {
    method,
    headers: { Accept: 'application/json', ...buildAclContextHeaders(), ...(json ? { 'Content-Type': 'application/json' } : {}) },
    ...(json ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) { throw new ServiceDeleteError(data?.error || `Request failed (${response.status})`, response.status, data); }
  return data || {};
}

export const getDeletePlan = (projectId, service) => call(url(projectId, `/${encodeURIComponent(short(service))}/delete-plan`));
export const deleteService = (projectId, service, { confirm }) => call(url(projectId, `/${encodeURIComponent(short(service))}`), { method: 'DELETE', body: { confirm } });
export const listDeletedServices = async(projectId) => (await call(url(projectId, '/deleted'))).deleted || [];
export const restoreDeletedService = (projectId, id) => call(url(projectId, `/deleted/${encodeURIComponent(id)}/restore`), { method: 'POST' });
